<template>
  <div class="trk-panel mb-3">
    <div class="trk-panel-head trk-panel-head-split">
      <div>
        <h2><i class="fa fa-credit-card"></i> Payment problems</h2>
        <p>Every failed payment with the exact reason from Stripe or our backend — declined cards at checkout,
          failed consultation captures and failed additional payments.</p>
      </div>
      <div class="trk-panel-actions">
        <router-link class="btn trk-btn-ghost btn-sm" to="/orders/payment-failed">Failed payments page</router-link>
      </div>
    </div>

    <div class="trk-panel-body">
      <div class="pp-filters">
        <button v-for="k in kinds" :key="k.value" type="button" class="pp-kind"
                :class="{ active: kind === k.value }" @click="setKind(k.value)">
          {{ k.label }} <span class="pp-count">{{ kindCount(k.value) }}</span>
        </button>
        <select class="form-control form-control-sm pp-code" v-model="code" title="Only this exact reason">
          <option value="">All reasons</option>
          <option v-for="g in codeOptions" :key="g.kind + g.code" :value="g.code">
            {{ reasonLabel(g) }} ({{ g.count }})
          </option>
        </select>
        <span v-if="loading" class="text-muted small"><b-spinner small /> Loading…</span>
      </div>

      <div v-if="data.truncated" class="text-muted small mb-2">
        Showing the latest 1,000 failures in this range — narrow the dates for full counts.
      </div>

      <div class="pp-groups" v-if="visibleGroups.length">
        <button v-for="g in visibleGroups" :key="g.kind + g.code" type="button" class="pp-group"
                :class="{ active: code === g.code }" @click="code = code === g.code ? '' : g.code">
          <span class="pp-group-count">{{ g.count }}</span>
          <span class="pp-group-text">
            <strong>{{ reasonLabel(g) }}</strong>
            <span class="text-muted small">{{ kindLabel(g.kind) }} · <code>{{ g.code }}</code></span>
          </span>
        </button>
      </div>
    </div>

    <div class="trk-panel-body p-0">
      <div class="trk-table-scroll">
        <table class="table trk-table trk-table-clickable mb-0">
          <thead>
            <tr>
              <th>When</th>
              <th>Why it failed</th>
              <th>Where</th>
              <th>Patient</th>
              <th>Order</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in cases" :key="c.id" @click="openId = c.id" title="Click for every detail">
              <td class="text-nowrap">{{ when(c.createdAt) }}</td>
              <td>
                <strong>{{ reasonLabel(c) }}</strong>
                <div><code v-if="c.code">{{ c.code }}</code></div>
                <div class="text-danger small" v-if="c.message && c.message !== reasonLabel(c)" :title="c.message">{{ short(c.message) }}</div>
              </td>
              <td>
                <span class="trk-pill" :class="c.kind === 'CHECKOUT' ? 'info' : 'bad'">{{ kindLabel(c.kind) }}</span>
                <div class="text-muted small">{{ c.paymentFor }}<span v-if="c.source"> · {{ sourceLabel(c.source) }}</span></div>
                <div class="text-muted small" v-if="c.amountPence">{{ money(c.amountPence) }}</div>
              </td>
              <td>
                <router-link v-if="c.patientId" :to="'/patient/' + c.patientId" @click.native.stop>{{ c.patientName || ('Patient #' + c.patientId) }}</router-link>
                <span v-else class="text-muted">Not signed in</span>
                <div class="text-muted small" v-if="c.email">{{ c.email }}</div>
                <div><router-link v-if="c.sessionId" class="small" :to="'/tracking/sessions/' + c.sessionId" @click.native.stop>Journey</router-link></div>
              </td>
              <td>
                <router-link v-if="c.orderItemId" :to="orderLink(c)" @click.native.stop>#{{ c.orderItemId }}</router-link>
                <span v-else class="text-muted small">No order yet</span>
              </td>
              <td class="text-right" @click.stop>
                <button type="button" class="btn trk-btn-ghost btn-sm" @click="openId = c.id">Details</button>
              </td>
            </tr>
            <tr v-if="!cases.length && !loading">
              <td colspan="6">
                <div class="trk-empty"><strong>No payment failures</strong>Nothing failed for these filters in this date range.</div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <payment-problem-detail :problem-id="openId" @close="openId = null" />
  </div>
</template>

<script>
import moment from 'moment';
import PaymentProblemDetail from './PaymentProblemDetail.vue';

/** Plain-English versions of the Stripe codes (same wording as the Failed payments page). */
const REASONS = {
  insufficient_funds: 'Not enough money in the account',
  card_declined: 'Card declined by the bank',
  generic_decline: 'Card declined by the bank (no reason given)',
  do_not_honor: 'Bank refused the payment',
  expired_card: 'Card has expired',
  incorrect_cvc: 'Security code was wrong',
  invalid_cvc: 'Security code was wrong',
  incorrect_number: 'Card number was wrong',
  invalid_number: 'Card number was wrong',
  incomplete_number: 'Card number is incomplete',
  incomplete_cvc: 'Security code is incomplete',
  incomplete_expiry: 'Expiry date is incomplete',
  invalid_expiry_year: 'Expiry year is wrong',
  invalid_expiry_month: 'Expiry month is wrong',
  lost_card: 'Card reported lost',
  stolen_card: 'Card reported stolen',
  fraudulent: 'Blocked as suspected fraud',
  card_velocity_exceeded: 'Card spending limit reached',
  authentication_required: 'Bank needs the patient to approve it (3D Secure)',
  payment_intent_authentication_failure: 'Patient did not complete 3D Secure',
  setup_intent_authentication_failure: 'Patient did not complete 3D Secure',
  processing_error: 'Stripe processing error',
  wallet_cancelled: 'Patient closed Apple Pay / Google Pay',
  booking_failed: 'Card accepted but the booking could not be saved',
  setup_intent_failed: 'Could not start the payment (our server)',
  stripe_not_loaded: 'Payment form did not load',
  no_saved_card: 'No saved card to charge',
  unknown: 'Reason not recorded'
};

const KIND_LABELS = { CHECKOUT: 'Checkout', CONSULTATION: 'Consultation payment', ADDITIONAL: 'Additional payment' };

export default {
  components: { PaymentProblemDetail },
  props: {
    fromDate: { type: String, required: true },
    toDate: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      kind: '',
      code: '',
      openId: null,
      data: { total: 0, byKind: {}, groups: [], cases: [] }
    };
  },
  computed: {
    kinds () {
      return [
        { value: '', label: 'All' },
        { value: 'CHECKOUT', label: 'Checkout' },
        { value: 'CONSULTATION', label: 'Consultation' },
        { value: 'ADDITIONAL', label: 'Additional' }
      ];
    },
    visibleGroups () {
      return (this.data.groups || []).filter((g) => !this.kind || g.kind === this.kind);
    },
    codeOptions () {
      const seen = {};
      return this.visibleGroups.filter((g) => {
        if (seen[g.code]) return false;
        seen[g.code] = true;
        return true;
      });
    },
    cases () {
      return this.data.cases || [];
    }
  },
  watch: {
    fromDate: 'load',
    toDate: 'load',
    code: 'load'
  },
  created () {
    this.load();
  },
  methods: {
    load () {
      this.loading = true;
      this.$orderService.getPaymentProblems({ from: this.fromDate, to: this.toDate, kind: this.kind, code: this.code })
        .then((res) => {
          this.data = (res && res.data) || { groups: [], cases: [] };
        })
        .catch(() => {
          this.data = { total: 0, byKind: {}, groups: [], cases: [] };
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setKind (kind) {
      this.kind = kind;
      if (this.code && !this.visibleGroups.some((g) => g.code === this.code)) {
        this.code = ''; // triggers load
      } else {
        this.load();
      }
    },
    kindCount (kind) {
      const by = this.data.byKind || {};
      return kind ? (by[kind] || 0) : (this.data.total || 0);
    },
    kindLabel (kind) {
      return KIND_LABELS[kind] || kind;
    },
    reasonLabel (item) {
      const code = item.code || 'unknown';
      return REASONS[code] || item.label || (item.sampleMessage || item.message) || code.replace(/_/g, ' ');
    },
    sourceLabel (source) {
      return {
        CARD: 'card',
        GOOGLE: 'Google Pay',
        GOOGLE_PAY: 'Google Pay',
        APPLE: 'Apple Pay',
        APPLE_PAY: 'Apple Pay',
        ONLINE: 'card',
        DOCTOR_COMPLETE: 'when the doctor completed the consult',
        PAYMENT_SERVICE_PREAUTH: 'card pre-authorisation',
        PAYMENT_SERVICE_CAPTURE: 'card capture'
      }[source] || source.toLowerCase().replace(/_/g, ' ');
    },
    orderLink (c) {
      return c.kind === 'CONSULTATION' ? '/order/payment-failed/' + c.orderItemId : '/order/' + c.orderItemId;
    },
    money (pence) {
      return (pence / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
    },
    when (value) {
      return value ? moment(value).local().format('DD/MM/YY HH:mm') : '-';
    },
    short (text) {
      return text && text.length > 120 ? text.slice(0, 120) + '…' : text;
    }
  }
};
</script>

<style scoped>
.pp-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.pp-kind {
  border: 1px solid #dfe7e3;
  background: #fff;
  border-radius: 999px;
  padding: 0.25rem 0.75rem;
  font-size: 0.85rem;
  cursor: pointer;
}
.pp-kind.active {
  background: #17333a;
  border-color: #17333a;
  color: #fff;
}
.pp-count {
  opacity: 0.7;
  margin-left: 0.2rem;
}
.pp-code {
  width: auto;
  max-width: 320px;
}
.pp-groups {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 0.5rem;
}
.pp-group {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  text-align: left;
  border: 1px solid #dfe7e3;
  background: #fff;
  border-radius: 8px;
  padding: 0.5rem 0.65rem;
  cursor: pointer;
}
.pp-group.active {
  border-color: #b42318;
  background: #fdecea;
}
.pp-group-count {
  font-size: 1.2rem;
  font-weight: 700;
  color: #b42318;
  min-width: 2ch;
}
.pp-group-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
</style>
